'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckoutRequest, Order, PaymentOptions } from '@akademi/site-builder-runtime';
import { formatDecimalMoney } from '@akademi/site-builder-blocks';

/**
 * Paying by bank transfer: the order is placed, the shopper is given the shop's
 * account and a reference, and nothing is sent until the money arrives. An order
 * still unpaid when the due date passes is cancelled by the shop.
 *
 * <p>These fields are Sorsy's, not the pinned runtime's, so they are declared
 * here as optional extensions of its types: a backend that does not know about
 * transfers simply never sends them, and the checkout looks as it always did.
 */
export interface BankTransferInstructions {
  accountName?: string | null;
  bankName?: string | null;
  sortCode?: string | null;
  accountNumber?: string | null;
  iban?: string | null;
  bic?: string | null;
  /** The order number — what the shopper must quote so the payment can be matched. */
  reference?: string | null;
  /** ISO instant. */
  dueAt?: string | null;
}

export type ShopPaymentOptions = PaymentOptions & { bankTransfer?: boolean };

export type TransferCheckoutRequest = CheckoutRequest & { paymentMethod?: 'CARD' | 'BANK_TRANSFER' };

type TransferOrder = Order & { paymentMethod?: string | null; bankTransfer?: BankTransferInstructions | null };

/** The instructions, only while the order is waiting for its transfer. */
export function bankTransferOf(order: Order | null): BankTransferInstructions | null {
  return (order as TransferOrder | null)?.bankTransfer ?? null;
}

export function BankTransferNotice({ order, transfer }: { order: Order; transfer: BankTransferInstructions }) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const due = dueDate(transfer.dueAt, i18n.language);
  const rows: Array<[string, string | null | undefined]> = [
    [t('commerce.transfer.accountName'), transfer.accountName],
    [t('commerce.transfer.bankName'), transfer.bankName],
    [t('commerce.transfer.sortCode'), transfer.sortCode],
    [t('commerce.transfer.accountNumber'), transfer.accountNumber],
    ['IBAN', transfer.iban],
    ['BIC', transfer.bic],
    [t('commerce.transfer.reference'), transfer.reference],
  ];
  return (
    <div style={boxStyle} role="status">
      <p style={{ margin: '0 0 12px', fontWeight: 600 }}>
        {t('commerce.transfer.lead', {
          amount: formatDecimalMoney(order.total, order.currency, i18n.language),
        })}
      </p>
      <dl style={listStyle}>
        {rows
          .filter(([, value]) => value != null && value.trim() !== '')
          .map(([label, value]) => (
            <div key={label} style={{ display: 'contents' }}>
              <dt style={termStyle}>{label}</dt>
              <dd style={valueStyle}>{value}</dd>
            </div>
          ))}
      </dl>
      <p style={{ margin: '12px 0 0' }}>
        {due ? t('commerce.transfer.due', { date: due }) : t('commerce.transfer.dueSoon')}
      </p>
    </div>
  );
}

function dueDate(iso: string | null | undefined, language: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(language || 'en-GB', {
      dateStyle: 'long',
      timeZone: 'Europe/London',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

const boxStyle: CSSProperties = {
  margin: '16px 0',
  padding: '16px 18px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f3f4f6)',
};
const listStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(0, 1fr)',
  gap: '6px 16px',
  margin: 0,
};
const termStyle: CSSProperties = { color: 'var(--color-text-secondary, #6b7280)' };
const valueStyle: CSSProperties = { margin: 0, fontWeight: 600, overflowWrap: 'anywhere' };
